/**
 * Copyright (C) 2019 Egylis,
 * Siège social, 83 bis rue de l'Abbé Groult, 75015 Paris - France.
 * All Rights Reserved.
 *
 * E-mail : info@egylis.com
 *
 * This software is the confidential and proprietary information
 * of Egylis. ("Confidential Information"). You shall not
 * disclose such Confidential Information and shall use it only
 * in accordance with the terms of the license agreement you
 * entered into with Egylis.
 *
 * Created by calvez on 20/03/19.
 *
 * Version : Generator 2.0.9
 */


import {Injectable} from '@angular/core';
import {StatusEnum} from '../entity';
import Dexie from 'dexie';
import {environment} from '../../environments/environment';
import {Log, LogAction, LogInfoType, LogLevelType, LogTrackType} from './entity';
import {CordovaBackService} from '../service/cordovaBackService';
import {Journal} from '../msa/entity/journal';
import {MsaService} from '../msa/msaService';
import {UtilMpaFunction} from '../mpa/utility';
import {NavigationEnd, Router} from '@angular/router';
import {Observable, of, Subscription} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {catchError} from 'rxjs/operators';
import * as StackTraceParser from 'error-stack-parser';


declare var gtag: any;

/**
 * Log Service
 */
@Injectable()
export class LogService {
    /**
     * Dexie Name
     */
    private static DEXIE_NAME: string = 'log';
    /**
     * Dexie Database
     */
    private dexieDb: Dexie;
    /**
     * Is started
     */
    private readonly _isStarted: boolean = false;
    /**
     * User
     * @type {string}
     */
    private lastUser: string = null;
    /**
     * User Id
     * @type {string}
     */
    private lastUserId: string = null;
    /**
     * User
     * @type {string}
     */
    private lastEvent: string = null;
    /**
     * User
     * @type {string}
     */
    private lastEventId: string = null;
    /**
     * Last Info Date
     * @type {Date}
     */
    private lastInfoDate: number = 0;
    /**
     * Page Subscription
     * @type {null}
     */
    private pageSubscription: Subscription = null;
    /**
     * Is Syncing
     */
    private isSyncing: boolean = false;
    /**
     * Log To Send
     * @type {Log[]}
     */
    private logsToSend: Array<Log> = [];
    /**
     * Log To Send
     * @type {Log[]}
     */
    private logsToSending: Array<Log> = [];
    /**
     * Connectivity Quality Sync
     */
    private connectivityQualityFunction: () => Promise<boolean>;
    /**
     * Connectivity Sync
     */
    private connectivityFunction: () => Promise<boolean>;

    /**
     * Constructor
     */
    constructor(private cordovaBackService: CordovaBackService,
                private http: HttpClient,
                private msaService: MsaService
    ) {
        this.dexieDb = new Dexie(environment.name + '_' + LogService.DEXIE_NAME + '_' + environment.mode);
        this.dexieDb.version(1).stores({
            log: 'ts'
        });
        this._isStarted = true;

        this.initData();
    }

    /**
     * Init Data
     */
    private initData() {
        console.log('*** LogService - initData');
        this.connectivityQualityFunction = this.defaultEventFunction;
        this.connectivityFunction = this.cordovaBackService.connectivityFunction;
        this.msaService.logService = this;
        const p1 = this.msaService.isReadyToStart();
        const p2 = this.cordovaBackService.isReadyToStart();
        Promise.all([p1, p2]).then(
            () => {
                // Log Start
                this.doLogInfo(LogInfoType.START);
            }
        );
    }

    /**
     * Log Info
     */
    public logInfo(): void {
        if (this.lastInfoDate + environment.logInfoTime < Date.now()) {
            this.lastInfoDate = Date.now();
            this.doLogInfo(LogInfoType.INFO);
        }
    }

    /**
     * Set Router
     * @param {Router} router
     */
    public setRouter(router: Router): void {
        if (this.pageSubscription != null) {
            this.pageSubscription.unsubscribe();
        }
        this.pageSubscription = router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                this.logTrackPage(event.urlAfterRedirects);
            }
        });
    }

    /**
     * Log log
     * @param {string} msg
     * @param {string} className
     * @param {string} methodName
     * @param {number} level
     */
    public logLog(msg: string, className: string, methodName: string, level: number): void {
        const log: Log = {
            ts: UtilMpaFunction.getTimeStampMadeInEgylis(),
            appDate: new Date(),
            appName: environment.name,
            appVersion: environment.version,
            appMode: environment.mode,
            user: this.lastUser,
            userId: this.lastUserId,
            event: this.lastEvent,
            eventId: this.lastEventId,
            deviceId: this.cordovaBackService.getDeviceId(),
            action: LogAction.LOG,
            type: level,
            value: className + '.' + methodName,
            info: msg
        };
        this.saveLog(log);
    }


    /**
     * Log log
     * @param {string} page
     */
    private logTrackPage(page: string): void {
        const log: Log = {
            ts: UtilMpaFunction.getTimeStampMadeInEgylis(),
            appDate: new Date(),
            appName: environment.name,
            appVersion: environment.version,
            appMode: environment.mode,
            user: this.lastUser,
            userId: this.lastUserId,
            event: this.lastEvent,
            eventId: this.lastEventId,
            deviceId: this.cordovaBackService.getDeviceId(),
            action: LogAction.TRACK,
            type: LogTrackType.PAGE,
            value: page,
            info: ''
        };
        if (environment.logGtag && typeof gtag !== 'undefined' && gtag) {
            const params: any = {
                page_path: page,
                page_location: window.location.protocol + '//' + window.location.host + page,
                app_name: environment.name,
                app_id: environment.name + '@' + environment.mode,
                app_version: environment.version
            };
            if (this.lastUserId != null) {
                params.user_id = this.lastUserId;
            }
            gtag('config', environment.logGtagId, params);
        }
        this.saveLog(log);
    }


    /**
     * Log Track Event
     * @param {string} action
     * @param {string} category
     * @param {string} label
     * @param {string} value
     */
    public logTrackEvent(category: string, action: string, label: string, value: string): void {
        const msg = {
            category,
            action,
            label,
            value
        };
        const log: Log = {
            ts: UtilMpaFunction.getTimeStampMadeInEgylis(),
            appDate: new Date(),
            appName: environment.name,
            appVersion: environment.version,
            appMode: environment.mode,
            user: this.lastUser,
            userId: this.lastUserId,
            event: this.lastEvent,
            eventId: this.lastEventId,
            deviceId: this.cordovaBackService.getDeviceId(),
            action: LogAction.TRACK,
            type: LogTrackType.EVENT,
            value: category + '.' + action,
            info: JSON.stringify(msg)
        };
        if (environment.logGtag && typeof gtag !== 'undefined' && gtag) {
            const properties: any = {
                event_category: category,
                event_label: label,
                value,
                app_name: environment.name,
                app_id: environment.name + '@' + environment.mode,
                app_version: environment.version
            };
            if (this.lastUserId != null) {
                properties.user_id = this.lastUserId;
            }
            gtag('event', action, properties);
        }
        this.saveLog(log);
    }

    /**
     * Log log
     * @param {string} msg
     * @param {string} className
     * @param {string} methodName
     * @param {number} level
     */
    public logError(msg: string, className: string, methodName: string, level: number): void {
        const log: Log = {
            ts: UtilMpaFunction.getTimeStampMadeInEgylis(),
            appDate: new Date(),
            appName: environment.name,
            appVersion: environment.version,
            appMode: environment.mode,
            user: this.lastUser,
            userId: this.lastUserId,
            event: this.lastEvent,
            eventId: this.lastEventId,
            deviceId: this.cordovaBackService.getDeviceId(),
            action: LogAction.ERROR,
            type: level,
            value: className + '.' + methodName,
            info: msg
        };
        this.saveLog(log);
    }

    public logErrorError(error: Error): void {
        const stackFrames = StackTraceParser.parse(error);
        let value: string = '';
        if (stackFrames.length > 0) {
            value = stackFrames[0].fileName + '@' + stackFrames[0].functionName + ':' + stackFrames[0].lineNumber;
        }
        const log: Log = {
            ts: UtilMpaFunction.getTimeStampMadeInEgylis(),
            appDate: new Date(),
            appName: environment.name,
            appVersion: environment.version,
            appMode: environment.mode,
            user: this.lastUser,
            userId: this.lastUserId,
            event: this.lastEvent,
            eventId: this.lastEventId,
            deviceId: this.cordovaBackService.getDeviceId(),
            action: LogAction.ERROR,
            type: LogLevelType.L1,
            value,
            info: JSON.stringify(stackFrames)
        };
        this.saveLog(log);
    }


    /**
     * Do log Info Type
     * @param {LogInfoType} type
     */
    private doLogInfo(type: LogInfoType): void {
        const log: Log = {
            ts: UtilMpaFunction.getTimeStampMadeInEgylis(),
            appDate: new Date(),
            appName: environment.name,
            appVersion: environment.version,
            appMode: environment.mode,
            user: this.lastUser,
            userId: this.lastUserId,
            event: this.lastEvent,
            eventId: this.lastEventId,
            deviceId: this.cordovaBackService.getDeviceId(),
            action: LogAction.INFO,
            type,
            value: '',
            info: JSON.stringify(this.cordovaBackService.geInfo())
        };
        this.saveLog(log);
    }

    /**
     * Return a promise who resolve when it's ready to start.
     * @return {Promise<StatusEnum>}
     */
    public isReadyToStart(): Promise<StatusEnum> {
        console.log('=========== LogService isReadyToStart');
        return new Promise(
            (resolve) => {
                this.isStartedTimeout(resolve);
            });
    }

    /**
     * is Started with a timeout
     * @param promiseResolve
     */
    private isStartedTimeout(promiseResolve: (r: StatusEnum) => void): void {
        console.log('-- ConfigService isStartedTimeout');
        if (this._isStarted) {
            console.log('-- LogService isStartedTimeout -- OK');
            promiseResolve(StatusEnum.OK);
        } else {
            console.log('-- LogService isStartedTimeout -- TimeOut');
            setTimeout(this.isStartedTimeout.bind(this, promiseResolve), 100);
        }
    }

    /**
     * Save a log
     * @param {Log} log
     */
    private saveLog(log: Log): void {
        // Faire la méthode
        // @ts-ignore
        if (environment.log.console[log.action]) {
            if (
                (
                    (log.action === LogAction.LOG || log.action === LogAction.ERROR)
                    && (environment.log.console as any)[log.action + '_LEVEL'] >= log.type
                )
                || log.action === LogAction.INFO
                || log.action === LogAction.TRACK
            ) {
                switch (log.action) {
                    case LogAction.LOG:
                        console.log('###', log.action, log.type, log.value, log.info);
                        break;
                    case LogAction.ERROR:
                        console.error('###', log.action, log.type, log.value, log.info);
                        break;
                    case LogAction.INFO:
                        // tslint:disable-next-line:no-console
                        console.info('###', log.action, log.type, log.value, log.info);
                        break;
                    case LogAction.TRACK:
                        console.warn('###', log.action, log.type, log.value, log.info);
                        break;
                }

            }
        }
        if ((environment.log.server as any)[log.action]) {
            if (
                (
                    (log.action === LogAction.LOG || log.action === LogAction.ERROR)
                    && (environment.log.server as any)[log.action + '_LEVEL'] >= log.type
                )
                || log.action === LogAction.INFO
                || log.action === LogAction.TRACK
            ) {
                this.logsToSend.push(log);
                this.synchronize();
            }
        }
    }

    /**
     * Synchronization.
     */
    private synchronize(): void {
        console.log('<==>  LogService Synchronize');
        // this.logLog('*** LogService - synchronize', 'LogService', 'synchronize', 3);
        if (!this.isSyncing) {
            this.isSyncing = true;
            this.logsToSending = [...this.logsToSend];
            this.logsToSend = [];
            this.synchronizePart1Connectivity();
        } else {
            // this.logLog('*** LogService - synchronize - $$$$$$$$$$$$  Pas de nouvelles synchro car déjà commencé',
            //     'LogService', 'synchronize', 3);
        }
    }

    /**
     * Connectivity Compute
     */
    private synchronizePart1Connectivity(): void {
        this.connectivityFunction().then(
            (res) => {
                if (res) {
                    this.sendDataToServerDo().then(
                        () => {
                            this.continueWithOldLogs();
                        },
                        () => {
                            this.saveSendingLogToDatabase().then(
                                () => {
                                    this.isSyncing = false;
                                },
                                () => {
                                    this.isSyncing = false;
                                }
                            );
                        });
                } else {
                    this.saveSendingLogToDatabase().then(
                        () => {
                            this.isSyncing = false;
                        },
                        () => {
                            this.isSyncing = false;
                        }
                    );
                }
            },
            () => {
                this.saveSendingLogToDatabase().then(
                    () => {
                        this.isSyncing = false;
                    },
                    () => {
                        this.isSyncing = false;
                    }
                );
            });
    }

    /**
     * Save logs to database
     */
    private saveSendingLogToDatabase(): Promise<boolean> {
        return new Promise(
            (resolve) => {
                this.dexieDb.table('log').bulkAdd(this.logsToSending).then(
                    () => {
                        this.logsToSending = [];
                        resolve(true);
                    },
                    () => {
                        // Nothing
                    }
                );
            });
    }

    private continueWithOldLogs(): void {
        this.getOldLogFromDatabase().then(
            logs => {
                if (logs.length > 0) {
                    this.logsToSending = [...logs];
                    this.sendDataToServerDo().then(
                        () => {
                            this.continueWithOldLogs();
                        },
                        () => {
                            this.saveSendingLogToDatabase().then(
                                () => {
                                    this.isSyncing = false;
                                },
                                () => {
                                    this.isSyncing = false;
                                }
                            );
                        });
                } else {
                    this.isSyncing = false;
                }
            },
            () => {
                this.isSyncing = false;
            }
        );
    }

    /**
     * Get Old Log From Database
     * @returns {Promise<Array<Log>>}
     */
    private getOldLogFromDatabase(): Promise<Array<Log>> {
        return new Promise(
            (resolve, reject) => {
                this.dexieDb.table('log').limit(42)
                    .toArray()
                    .then(
                        succ => {
                            this.dexieDb.table('log').limit(42).delete().catch(
                                () => {
                                }
                            );
                            resolve(<Array<Log>>succ);
                        },
                        () => {
                            // Nothing
                            reject();
                        }
                    );
            });
    }

    /**
     * Sending Dat to Server
     * @returns {Promise<boolean>}
     */
    private sendDataToServerDo(): Promise<boolean> {
        return new Promise(
            (resolve, reject) => {
                const url = environment.logServerUrl
                    + 'geo/rest/blabla/saves';
                this.http.post<Array<Journal>>(url, this.logsToSending)
                    .pipe(catchError(this.handleError('post', [], reject)))
                    .subscribe(
                        () => {
                            this.logsToSending = [];
                            resolve(true);
                        }
                    );
            }
        );
    }

    /**
     * Error
     * @param operation operation
     * @param result result
     * @param reject reject function
     */
    private handleError<T>(operation = 'operation', result: T, reject: (reason?: any) => void) {
        return (error: any): Observable<T> => {
            // this.logError('-- LogService handleError Operation ' + operation + ' Error ' + JSON.stringify(error),
            //     'LogService', 'handleError', 1);
            console.error('-- LogService handleError Operation', operation, error);
            reject();
            return of(result as T);
        };
    }

    /**
     * Event Function for Item Sync.
     */
    public defaultEventFunction(): Promise<boolean> {
        return new Promise(
            (resolve) => {
                resolve(true);
            });
    }

    /**
     * set eventItemSyncFunction
     * @param eventFunction
     */
    public setEventConnectivityQuality(eventFunction: () => Promise<boolean>): void {
        this.connectivityQualityFunction = eventFunction;
    }

    /**
     * set eventItemSyncFunction
     * @param eventFunction
     */
    public setEventConnectivity(eventFunction: () => Promise<boolean>): void {
        this.connectivityFunction = eventFunction;
    }

    /**
     * Set User
     * @param {string} user
     * @param {string} userId
     */
    public setUser(user: string, userId: string): void {
        this.lastUser = user;
        this.lastUserId = userId;
    }

    /**
     * Set Event
     * @param {string} event
     * @param {string} eventId
     */
    public setEvent(event: string, eventId: string): void {
        this.lastEvent = event;
        this.lastEventId = eventId;
    }
}
